import { createContext, FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface Settings {
  theme: 'light' | 'dark';
  lang: 'en' | 'ja';
  subscribed: boolean;
}

export interface SettingsContextValue {
  settings: Settings;
  saveSettings: (update: Settings) => void;
}

interface SettingsProviderProps {
  children: ReactNode;
}

const initialSettings: Settings = {
  theme: 'light',
  lang: 'ja',
  subscribed: false,
};

export const restoreSettings = (): Settings | null => {
  let settings = null;

  try {
    const storedData: string | null = globalThis.localStorage.getItem('ci-tweet-app.settings');

    if (storedData) {
      settings = JSON.parse(storedData) as Settings;
    } else {
      settings = {
        theme: globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
        lang: 'ja',
      };
    }
  } catch (err) {
    // If stored data is not a strigified JSON this will fail,
    // that's why we catch the error
  }

  return settings as Settings;
};

export const storeSettings = (settings: Settings): void => {
  globalThis.localStorage.setItem('ci-tweet-app.settings', JSON.stringify(settings));
};

export const SettingsContext = createContext<SettingsContextValue>({
  settings: initialSettings,
  saveSettings: () => {
    // do nothing
  },
});

export const SettingsProvider: FC<SettingsProviderProps> = (props) => {
  const { children } = props;
  const { i18n } = useTranslation();
  const [settings, setSettings] = useState<Settings>(initialSettings);

  useEffect(() => {
    const restoredSettings = restoreSettings();
    if (restoredSettings) {
      setSettings({ ...initialSettings, ...restoredSettings });
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      i18n.changeLanguage(restoredSettings.lang);
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    navigator.serviceWorker.ready.then((registration) => {
      // Check if the user has an existing subscription
      registration.pushManager
        .getSubscription()
        .then((existing) => {
          if (existing) {
            setSettings((prev) => ({ ...prev, subscribed: true }));
          }
        })
        .catch(() => {
          // do nothing
        });
    });
  }, [i18n]);

  const saveSettings = useCallback(
    (updatedSettings: Settings) => {
      setSettings(updatedSettings);
      storeSettings(updatedSettings);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      i18n.changeLanguage(updatedSettings.lang);
    },
    [i18n]
  );

  const value = useMemo(() => ({ settings, saveSettings }), [settings, saveSettings]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const SettingsConsumer = SettingsContext.Consumer;
